// CaseTopBar.tsx
import React from "react";
import StarIcon from "@mui/icons-material/Star";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import { TabType } from "../../types/CaseTypes";
import { BackButton, PrimaryButton } from "../shared/helpers/GlobalStylesv5";
import { Avatar, Tooltip } from "@mui/material";
import { tokens } from "../../theme/tokens";

const TAB_CONFIG = {
  case: { icon: DescriptionOutlinedIcon, title: "Case Information" },
  docs: { icon: PictureAsPdfOutlinedIcon, title: "Documents" },
  invoice: { icon: ReceiptOutlinedIcon, title: "Invoice" },
  tasks: { icon: ChecklistOutlinedIcon, title: "Tasks" },
} as const;

interface CaseTopBarProps {
  caseId: string;
  userInCaseOpen: {
    showAvatar: boolean;
    message: string;
    primary: boolean;
  };
  tabState: TabType;
  onTabChange: (tab: TabType) => void;
  isDirty: boolean;
  isSubmitting: boolean;
  loaderState: boolean;
  caseStatus?: string;
}

const CaseTopBar: React.FC<CaseTopBarProps> = ({
  caseId,
  userInCaseOpen,
  tabState,
  onTabChange,
  isDirty,
  isSubmitting,
  loaderState,
  caseStatus,
}) => {
  const { otherAvatarColor, avatarColor } = useStore();
  const history = useHistory();

  const isActive = (caseStatus ?? "").toLowerCase() === "active";

  return (
    <div
      style={{
        backgroundColor: tokens.color.surface.default,
        borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.space[3],
          padding: `${tokens.space[2]} ${tokens.space[4]}`,
          flexWrap: "wrap",
        }}
      >
        <BackButton
          onClick={() => history.push("/app/cases", { return: true })}
        >
          <ArrowBackIcon />
        </BackButton>

        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
          <span
            style={{
              fontSize: tokens.type.caption.size,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: tokens.color.text.secondary,
              fontWeight: tokens.type.denseStrong.weight,
            }}
          >
            File #
          </span>
          <span
            style={{
              fontSize: "1.0625rem",
              fontWeight: 650,
              letterSpacing: "-0.01em",
              color: tokens.color.text.primary,
            }}
          >
            {caseId}
          </span>
        </div>

        {caseStatus ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: tokens.space[1],
              height: "24px",
              padding: `0 ${tokens.space[2]}`,
              borderRadius: tokens.radius.full,
              fontSize: tokens.type.caption.size,
              fontWeight: tokens.type.denseStrong.weight,
              backgroundColor: tokens.color.surface.subtle,
              color: isActive
                ? tokens.color.status.success
                : tokens.color.text.secondary,
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: tokens.radius.full,
                backgroundColor: isActive
                  ? tokens.color.status.success
                  : tokens.color.text.disabled,
              }}
            />
            {isActive ? "Active" : "Inactive"}
          </span>
        ) : null}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: tokens.space[3] }}>
          {/* Collaboration Avatar */}
          {userInCaseOpen.showAvatar && (
            <Tooltip title={userInCaseOpen.message}>
              <span style={{ display: "flex", alignItems: "center" }}>
                {userInCaseOpen.primary && (
                  <StarIcon
                    style={{
                      color: tokens.color.brand.primary,
                      marginRight: tokens.space[1],
                    }}
                  />
                )}
                <Avatar
                  style={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: userInCaseOpen.primary
                      ? avatarColor
                      : otherAvatarColor,
                  }}
                />
              </span>
            </Tooltip>
          )}

          {/* Save Changes Button */}
          {isDirty && tabState === "case" && (
            <PrimaryButton disabled={loaderState || isSubmitting} type="submit">
              Save changes
            </PrimaryButton>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div
        style={{
          display: "flex",
          gap: tokens.space[1],
          padding: `0 ${tokens.space[4]}`,
          overflowX: "auto",
        }}
      >
        {(Object.keys(TAB_CONFIG) as TabType[]).map((tab) => {
          const IconComponent = TAB_CONFIG[tab].icon;
          const active = tabState === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => onTabChange(tab)}
              style={{
                appearance: "none",
                background: "transparent",
                border: "none",
                borderBottom: `2px solid ${
                  active ? tokens.color.brand.primaryAction : "transparent"
                }`,
                cursor: "pointer",
                font: "inherit",
                fontSize: tokens.type.dense.size,
                fontWeight: active
                  ? 650
                  : tokens.type.denseStrong.weight,
                color: active
                  ? tokens.color.brand.primaryAction
                  : tokens.color.text.secondary,
                padding: `${tokens.space[3]} ${tokens.space[3]}`,
                display: "inline-flex",
                alignItems: "center",
                gap: tokens.space[2],
                whiteSpace: "nowrap",
              }}
            >
              <IconComponent style={{ width: "0.95em", height: "0.95em" }} />
              {TAB_CONFIG[tab].title}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CaseTopBar;
